import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type ConsumerTradeIn, TradeInDetailDialog, tradeInProgress } from '../components/TradeInDetailDialog'
import { type VouchersQuery, useConsumerTradeInsQuery, useVouchersQuery } from '../generated/graphql'
import { useTranslation } from '../localization/useTranslation'
import { useAuthStore } from '../store/auth'
import { useSetPageTitle } from '../store/pageTitle'
import { IconArrowLeft, IconGift } from '../ui/icons'
import { money } from '../util/money'
import { useCurrencySuffix } from '../util/useMoney'

type Voucher = VouchersQuery['Vouchers'][number]

const localeTag: Record<string, string> = { da: 'da-DK', en: 'en-GB', de: 'de-DE' }

/** Newest first — ISO date strings sort chronologically as plain strings. */
function byNewest<T extends { createdAt?: string | null; dateTime?: string | null }>(a: T, b: T): number {
  return (b.createdAt ?? b.dateTime ?? '').localeCompare(a.createdAt ?? a.dateTime ?? '')
}

/**
 * The trade-in's total value: the issued voucher amount when present (the actual reward), otherwise
 * the sum of the item trade-in values (the estimate, before the voucher is issued).
 */
function tradeInValue(ti: ConsumerTradeIn): number {
  const voucherTotal = (ti.vouchers ?? []).reduce((sum, v) => sum + (v?.value ?? 0), 0)
  if (voucherTotal > 0) return voucherTotal
  return (ti.items ?? []).reduce((sum, i) => sum + (i?.value ?? 0), 0)
}

/**
 * "My trade-ins" — the customer's account overview, gated to real accounts (see TopbarMenu). Two
 * sections: their **vouchers** first (what they can spend now / already spent), then their **trade-in
 * history** as clickable cards. Clicking a trade-in opens a detail dialog with the traded-in items and
 * the send-in code/QR/label again.
 *
 * Vouchers shown are only ever activated, non-disabled ones: an *available* voucher has
 * `currentValue > 0`; a *spent* one has run down to 0 (values can go negative — we floor at 0).
 */
export function MyTradeInsPage() {
  const { t, locale } = useTranslation()
  const navigate = useNavigate()
  const config = useAuthStore((s) => s.config)
  const consumerId = useAuthStore((s) => s.consumerId)
  const suffixFor = useCurrencySuffix()
  const [selected, setSelected] = useState<ConsumerTradeIn | null>(null)
  useSetPageTitle(t('myTradeIns.title'), t('myTradeIns.subtitle'))

  const { data: voucherData, loading: vLoading } = useVouchersQuery({
    variables: { consumerId: consumerId ?? '' },
    skip: !consumerId,
    fetchPolicy: 'cache-and-network',
  })
  const { data: tradeInData, loading: tLoading } = useConsumerTradeInsQuery({
    skip: !consumerId,
    fetchPolicy: 'cache-and-network',
  })

  const allVouchers = voucherData?.Vouchers ?? []
  const usable = allVouchers.filter((v) => v.activated && !v.disabled)
  const active = usable.filter((v) => (v.currentValue ?? 0) > 0).sort(byNewest)
  const spent = usable.filter((v) => (v.currentValue ?? 0) <= 0).sort(byNewest)

  const tradeIns = (tradeInData?.ConsumerTradeIns ?? []).flatMap((x) => (x ? [x] : [])).sort(byNewest)

  const fmtExpiry = (iso?: string | null): string | null => {
    if (!iso) return null
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return null
    return new Intl.DateTimeFormat(localeTag[locale] ?? 'en-GB', {
      month: '2-digit',
      year: 'numeric',
    }).format(d)
  }

  const fmtDate = (iso?: string | null): string => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return new Intl.DateTimeFormat(localeTag[locale] ?? 'en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(d)
  }

  const renderVoucher = (v: Voucher, spentRow: boolean) => {
    const amount = spentRow ? 0 : (v.currentValue ?? 0)
    const expiry = spentRow ? null : fmtExpiry(v.expireDate)
    return (
      <div className={`voucher${spentRow ? ' voucher--spent' : ''}`} key={v._id}>
        <span className="voucher-icon">
          <IconGift />
        </span>
        <div className="voucher-body">
          <div className="voucher-amt num">
            {money(amount)} {suffixFor(v.currency || config.currency)}
          </div>
          {v.code && <div className="voucher-code">{v.code}</div>}
          {expiry && <div className="voucher-meta">{t('rewards.expires', { date: expiry })}</div>}
        </div>
        <span className={`voucher-badge${spentRow ? ' voucher-badge--spent' : ''}`}>
          {spentRow ? t('rewards.spentBadge') : t('rewards.active')}
        </span>
      </div>
    )
  }

  const renderTradeInCard = (ti: ConsumerTradeIn) => {
    const items = (ti.items ?? []).flatMap((i) => (i ? [i] : []))
    // The first available item image (imageUrls, or a search item's masterVariantImageUrl).
    const firstImage = items.map((i) => i.imageUrls?.[0] || i.masterVariantImageUrl).find(Boolean)
    const count = items.length
    const value = tradeInValue(ti)
    const currency = ti.vouchers?.[0]?.currency || items[0]?.currency || config.currency
    const progress = tradeInProgress(ti)
    return (
      <button type="button" className="ti-card" key={ti._id} onClick={() => setSelected(ti)}>
        <div className="ti-thumb-wrap">
          {firstImage ? (
            <img className="ti-thumb" src={firstImage} alt="" />
          ) : (
            <span className="ti-thumb ti-thumb--empty" aria-hidden />
          )}
          {/* One image for the whole trade-in; the badge counts the items inside it. */}
          {count > 1 && (
            <span className="ti-count" aria-label={t('myTradeIns.itemsCount', { count })}>
              {count}
            </span>
          )}
        </div>
        <div className="ti-info">
          <div className="ti-date">
            {t('myTradeIns.tradeInPrefix')} {fmtDate(ti.dateTime)}
          </div>
          <div className={`ti-status${progress === 'received' ? ' ti-status--done' : ''}`}>
            {progress === 'received'
              ? t('myTradeIns.statusReceived')
              : progress === 'transit'
                ? t('myTradeIns.statusOnItsWay')
                : t('myTradeIns.statusAwaiting')}
          </div>
        </div>
        {value > 0 && (
          <div className="ti-value num">
            {money(value)} {suffixFor(currency)}
          </div>
        )}
      </button>
    )
  }

  const loading = vLoading || tLoading
  const nothing = !loading && active.length === 0 && spent.length === 0 && tradeIns.length === 0

  return (
    <div className="step rewards mytradeins">
      <button type="button" className="rewards-back" onClick={() => navigate('/items')}>
        <IconArrowLeft />
        {t('myTradeIns.back')}
      </button>

      {loading && allVouchers.length === 0 && tradeIns.length === 0 ? (
        <div className="center-note">
          <p>{t('myTradeIns.loading')}</p>
        </div>
      ) : nothing ? (
        <div className="center-note">
          <p>{t('myTradeIns.empty')}</p>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <section className="rewards-section">
              <h3 className="rewards-section-title">{t('rewards.activeHeading')}</h3>
              <div className="rewards-list">{active.map((v) => renderVoucher(v, false))}</div>
            </section>
          )}
          {spent.length > 0 && (
            <section className="rewards-section">
              <h3 className="rewards-section-title">{t('rewards.spentHeading')}</h3>
              <div className="rewards-list">{spent.map((v) => renderVoucher(v, true))}</div>
            </section>
          )}
          {tradeIns.length > 0 && (
            <section className="rewards-section">
              <h3 className="rewards-section-title">{t('myTradeIns.historyHeading')}</h3>
              <div className="ti-list">{tradeIns.map(renderTradeInCard)}</div>
            </section>
          )}
        </>
      )}

      <TradeInDetailDialog tradeIn={selected} onClose={() => setSelected(null)} />
    </div>
  )
}
